import { z } from 'zod';

import { apiResponseSchema, deletedEntityDataSchema } from './api-response';
import {
  accessCodeSchema,
  appUserDraftSchema,
  appUserSchema,
  bulkJobSchema,
  bulkUploadDraftSchema,
  controllerVendorDraftSchema,
  controllerVendorSchema,
  dashboardSnapshotSchema,
  groupAccessCodeSchema,
  hospitalityEventDraftSchema,
  hospitalityEventSchema,
  groupCodeDraftSchema,
  locationDraftSchema,
  locationSchema,
  manualCodeDraftSchema,
  pmsVendorDraftSchema,
  pmsVendorSchema,
  userScopeSchema,
  unitDraftSchema,
  unitSchema,
  vendorBindingSchema,
  tenantDraftSchema,
  tenantSchema
} from './hospitality';
import {
  pagedAccessCodeSchema,
  pagedBulkJobSchema,
  pagedControllerVendorSchema,
  pagedEventNetworkSchema,
  pagedEventSchema,
  pagedLocationSchema,
  pagedPmsVendorSchema,
  pagedUnitSchema,
  pagedTenantSchema,
  pagedUserSchema
} from './hospitality-tables';

export const sortDirectionParamSchema = z.enum(['asc', 'desc']);
export const pageParamSchema = z.coerce.number().int().min(1).default(1);
export const pageSizeParamSchema = z.coerce.number().int().min(1).default(10);

function tableQuerySchema<T extends readonly [string, ...string[]]>(sortKeys: T) {
  return z.object({
    page: pageParamSchema,
    pageSize: pageSizeParamSchema,
    sortKey: z.enum(sortKeys).default(sortKeys[0]),
    sortDirection: sortDirectionParamSchema.default('asc')
  });
}

export const unitListSegmentParamSchema = z.enum(['all', 'room', 'shared']);
export const spaceListSegmentParamSchema = unitListSegmentParamSchema;
export const unitListSortKeyParamSchema = z.enum([
  'name',
  'type',
  'vlan',
  'ssid',
  'panMode',
  'capacity'
]);
export const spaceListSortKeyParamSchema = unitListSortKeyParamSchema;
export const activeCodeSortKeyParamSchema = z.enum([
  'guest',
  'space',
  'validity',
  'source',
  'devices'
]);
export const archivedCodeSortKeyParamSchema = z.enum(['guest', 'source', 'validity']);
export const bulkJobSortKeyParamSchema = z.enum(['job', 'createdAt', 'vlan', 'state']);
export const tenantSortKeyParamSchema = z.enum(['name']);
export const userSortKeyParamSchema = z.enum(['name', 'tenants', 'locations']);
export const locationSortKeyParamSchema = z.enum(['name', 'pms', 'controller']);
export const sourceFilterParamSchema = z.enum(['all', 'pms', 'reception', 'bulk']);
export const codeTypeParamSchema = z.literal('hotel');
export const tenantPathParamSchema = z.object({
  tenantId: z.string()
});
export const tenantLocationPathParamSchema = tenantPathParamSchema.extend({
  locationId: z.string()
});
export const tenantLocationUnitPathParamSchema = tenantLocationPathParamSchema.extend({
  spaceId: z.string()
});
export const tenantLocationCodePathParamSchema = tenantLocationPathParamSchema.extend({
  codeId: z.string()
});
export const userPathParamSchema = z.object({
  userId: z.string()
});
export const vendorPathParamSchema = z.object({
  vendorId: z.string()
});

export const listLocationUnitsQuerySchema = tableQuerySchema([
  'name',
  'type',
  'vlan',
  'ssid',
  'panMode',
  'capacity'
] as const).extend({
  segment: unitListSegmentParamSchema.optional()
});
export const listLocationSpacesQuerySchema = listLocationUnitsQuerySchema;

export const listActiveCodesQuerySchema = tableQuerySchema([
  'guest',
  'space',
  'validity',
  'source',
  'devices'
] as const).extend({
  sourceFilter: sourceFilterParamSchema.default('all'),
  search: z.string().default(''),
  codeType: codeTypeParamSchema.default('hotel')
});

export const listArchivedCodesQuerySchema = tableQuerySchema([
  'guest',
  'source',
  'validity'
] as const).extend({
  search: z.string().default(''),
  codeType: codeTypeParamSchema.default('hotel')
});

export const listBulkJobsQuerySchema = tableQuerySchema([
  'job',
  'createdAt',
  'vlan',
  'state'
] as const);

export const listTenantsQuerySchema = tableQuerySchema(['name'] as const);

export const listUsersQuerySchema = tableQuerySchema([
  'name',
  'tenants',
  'locations'
] as const);

export const listLocationsQuerySchema = tableQuerySchema([
  'name',
  'pms',
  'controller'
] as const).extend({
  tenantId: z.string()
});

export const listControllerVendorsQuerySchema = tableQuerySchema(['name'] as const);
export const listPmsVendorsQuerySchema = tableQuerySchema(['name'] as const);

export const createManualCodeRequestSchema = manualCodeDraftSchema.omit({
  locationId: true
});
export const createGroupCodeRequestSchema = groupCodeDraftSchema;
export type CreateGroupCodeRequest = z.infer<typeof createGroupCodeRequestSchema>;
export const createEventRequestSchema = hospitalityEventDraftSchema;
export const updateEventRequestSchema = hospitalityEventDraftSchema;
export const deliveryReceiptSchema = z.object({
  channel: z.literal('email'),
  deliveredAt: z.string(),
  codeId: z.string().optional(),
  eventId: z.string().optional()
});
export const extendCodeRequestSchema = z.object({
  hours: z.number().int().positive()
});
export const activeUnitNetworkQrResponseDataSchema = z.object({
  ssid: z.string(),
  passphrase: z.string(),
  qrPayload: z.string()
});

export const createBulkJobRequestSchema = bulkUploadDraftSchema.omit({
  locationId: true
});
export const createBulkJobResponseDataSchema = z.object({
  job: bulkJobSchema,
  codes: z.array(accessCodeSchema)
});

export const createTenantRequestSchema = tenantDraftSchema.omit({ id: true });
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;
export const updateTenantRequestSchema = tenantDraftSchema.omit({ id: true });
export type UpdateTenantRequest = z.infer<typeof updateTenantRequestSchema>;

const locationAdministrationRequestSchema = z.object({
  name: locationDraftSchema.shape.name,
  city: locationDraftSchema.shape.city,
  category: locationDraftSchema.shape.category,
  pms: vendorBindingSchema.optional(),
  controller: vendorBindingSchema.optional(),
  tvIntegration: locationDraftSchema.shape.tvIntegration,
  emailIntegration: locationDraftSchema.shape.emailIntegration,
  state: locationDraftSchema.shape.state,
  note: locationDraftSchema.shape.note,
  timezone: locationDraftSchema.shape.timezone
});

export const createLocationRequestSchema = locationAdministrationRequestSchema;
export type CreateLocationRequest = z.infer<typeof createLocationRequestSchema>;
export const updateLocationRequestSchema = locationAdministrationRequestSchema;
export type UpdateLocationRequest = z.infer<typeof updateLocationRequestSchema>;
export const bulkCreateLocationsRequestSchema = z.object({
  items: z.array(createLocationRequestSchema)
});
export type BulkCreateLocationsRequest = z.infer<typeof bulkCreateLocationsRequestSchema>;

const userAdministrationRequestSchema = z
  .object({
    name: appUserDraftSchema.shape.name,
    email: appUserDraftSchema.shape.email,
    role: appUserDraftSchema.shape.role,
    scopes: z.array(userScopeSchema).min(1),
    status: appUserDraftSchema.shape.status
  })
  .superRefine((value, ctx) => {
    const tenantIds = value.scopes.map((scope) => scope.tenantId);

    if (new Set(tenantIds).size !== tenantIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'User scopes must contain unique tenantIds.',
        path: ['scopes']
      });
    }

    const locationIds = value.scopes.flatMap((scope) => scope.locationIds);

    if (new Set(locationIds).size !== locationIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A locationId cannot appear in multiple user scopes.',
        path: ['scopes']
      });
    }
  });

export const createUserRequestSchema = userAdministrationRequestSchema;
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;
export const updateUserRequestSchema = createUserRequestSchema;
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const createControllerVendorRequestSchema = controllerVendorDraftSchema;
export type CreateControllerVendorRequest = z.infer<typeof createControllerVendorRequestSchema>;
export const updateControllerVendorRequestSchema = controllerVendorDraftSchema;
export type UpdateControllerVendorRequest = z.infer<typeof updateControllerVendorRequestSchema>;

export const createPmsVendorRequestSchema = pmsVendorDraftSchema;
export type CreatePmsVendorRequest = z.infer<typeof createPmsVendorRequestSchema>;
export const updatePmsVendorRequestSchema = pmsVendorDraftSchema;
export type UpdatePmsVendorRequest = z.infer<typeof updatePmsVendorRequestSchema>;

export const createUnitRequestSchema = unitDraftSchema.omit({
  id: true,
  locationId: true
});
export type CreateUnitRequest = z.infer<typeof createUnitRequestSchema>;
export const createSpaceRequestSchema = createUnitRequestSchema;
export type CreateSpaceRequest = CreateUnitRequest;
export const updateUnitRequestSchema = unitDraftSchema.omit({
  id: true,
  locationId: true
});
export type UpdateUnitRequest = z.infer<typeof updateUnitRequestSchema>;
export const updateSpaceRequestSchema = updateUnitRequestSchema;
export type UpdateSpaceRequest = UpdateUnitRequest;
export const bulkCreateUnitsRequestSchema = z.object({
  items: z.array(createUnitRequestSchema)
});
export type BulkCreateUnitsRequest = z.infer<typeof bulkCreateUnitsRequestSchema>;

function bulkCreateResponseDataSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    created: z.number().int().min(0),
    failed: z.number().int().min(0),
    items: z.array(itemSchema)
  });
}

export const dashboardSnapshotResponseSchema = apiResponseSchema(dashboardSnapshotSchema);
export const pagedUnitsResponseSchema = apiResponseSchema(pagedUnitSchema);
export const pagedSpacesResponseSchema = pagedUnitsResponseSchema;
export const pagedActiveCodesResponseSchema = apiResponseSchema(pagedAccessCodeSchema);
export const pagedArchivedCodesResponseSchema = apiResponseSchema(pagedAccessCodeSchema);
export const pagedEventsResponseSchema = apiResponseSchema(pagedEventSchema);
export const pagedEventNetworksResponseSchema = apiResponseSchema(pagedEventNetworkSchema);
export const pagedBulkJobsResponseSchema = apiResponseSchema(pagedBulkJobSchema);
export const pagedTenantsResponseSchema = apiResponseSchema(pagedTenantSchema);
export const pagedUsersResponseSchema = apiResponseSchema(pagedUserSchema);
export const pagedLocationsResponseSchema = apiResponseSchema(pagedLocationSchema);
export const pagedControllerVendorsResponseSchema = apiResponseSchema(pagedControllerVendorSchema);
export const pagedPmsVendorsResponseSchema = apiResponseSchema(pagedPmsVendorSchema);
export const controllerVendorOptionsResponseSchema = apiResponseSchema(
  z.array(controllerVendorSchema)
);
export const pmsVendorOptionsResponseSchema = apiResponseSchema(z.array(pmsVendorSchema));
export const activeUnitCodeResponseSchema = apiResponseSchema(accessCodeSchema);
export const activeUnitNetworkQrResponseSchema = apiResponseSchema(
  activeUnitNetworkQrResponseDataSchema
);
export const manualCodeResponseSchema = apiResponseSchema(accessCodeSchema);
export const groupCodeResponseSchema = apiResponseSchema(groupAccessCodeSchema);
export const eventResponseSchema = apiResponseSchema(hospitalityEventSchema);
export const deliveryReceiptResponseSchema = apiResponseSchema(deliveryReceiptSchema);
export const extendCodeResponseSchema = apiResponseSchema(accessCodeSchema);
export const revokeCodeResponseSchema = apiResponseSchema(accessCodeSchema);
export const createBulkJobResponseSchema = apiResponseSchema(createBulkJobResponseDataSchema);
export const tenantResponseSchema = apiResponseSchema(tenantSchema);
export const deleteTenantResponseSchema = apiResponseSchema(deletedEntityDataSchema);
export const locationResponseSchema = apiResponseSchema(locationSchema);
export const bulkCreateLocationsResponseSchema = apiResponseSchema(
  bulkCreateResponseDataSchema(locationSchema)
);
export const deleteLocationResponseSchema = apiResponseSchema(deletedEntityDataSchema);
export const userResponseSchema = apiResponseSchema(appUserSchema);
export const deleteUserResponseSchema = apiResponseSchema(deletedEntityDataSchema);
export const controllerVendorResponseSchema = apiResponseSchema(controllerVendorSchema);
export const pmsVendorResponseSchema = apiResponseSchema(pmsVendorSchema);
export const deleteControllerVendorResponseSchema = apiResponseSchema(deletedEntityDataSchema);
export const deletePmsVendorResponseSchema = apiResponseSchema(deletedEntityDataSchema);
export const unitResponseSchema = apiResponseSchema(unitSchema);
export const spaceResponseSchema = unitResponseSchema;
export const bulkCreateUnitsResponseSchema = apiResponseSchema(bulkCreateResponseDataSchema(unitSchema));
export const deleteUnitResponseSchema = apiResponseSchema(deletedEntityDataSchema);
export const deleteSpaceResponseSchema = deleteUnitResponseSchema;
